import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Chip,
  Avatar,
  Tooltip,
  IconButton
} from '@mui/material';
import { ExitToApp as LogoutIcon } from '@mui/icons-material';
import { useAuth as useAuthContext } from '../../contexts/AuthContext';
import {
  Dashboard as DashboardIcon,
  People as CustomersIcon,
  Receipt as InvoicesIcon,
  AccountBalance as BankingIcon,
  Assessment as ReportsIcon,
  CreditCard as ExpensesIcon,
  Hub as IntegrationIcon,
  Business as CompaniesHouseIcon,
  Timeline as MonitoringIcon,
  Flag as UKFlagIcon,
  Groups as EmployeesIcon,
  Edit as TransactionIcon,
  AccountTree as FinanceIcon,
  Calculate as VATIcon,
  SmartToy as AIIcon,
  Psychology as AgentIcon,
  ContactPhone as CRMIcon,
  Inventory as SupplyChainIcon,
  WorkOutline as ProjectManagementIcon,
  AutoAwesome as ExecutiveAIIcon,
  OpenInNew as ExternalLinkIcon,
  Security as SecurityIcon,
  Gavel as ComplianceIcon,
  History as AuditIcon,
  Notifications as AlertsIcon,
  Backup as BackupIcon,
  DeleteSweep as DataRetentionIcon,
  AdminPanelSettings as UsersIcon
} from '@mui/icons-material';

const DRAWER_WIDTH = 280;

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactElement;
  path: string;
  badge?: string;
  comingSoon?: boolean;
  external?: boolean;
  externalUrl?: string;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard'
  },
  {
    id: 'ai-chat',
    label: 'AI Chat',
    icon: <AIIcon />,
    path: '/ai-chat',
    badge: 'AI'
  },
  {
    id: 'financial-advisor',
    label: 'Financial Advisor',
    icon: <FinanceIcon />,
    path: '/financial-advisor',
    badge: 'NEW'
  },
  {
    id: 'document-scanner',
    label: 'Document Scanner',
    icon: <ExpensesIcon />,
    path: '/document-scanner',
    badge: 'NEW'
  },
  // 'AI Agents' is now the main Dashboard
  // {
  //   id: 'ai-agents',
  //   label: 'AI Agents',
  //   icon: <AgentIcon />,
  //   path: '/ai-agents',
  //   badge: 'NEW'
  // },
  {
    id: 'employees',
    label: 'Employee Management',
    icon: <EmployeesIcon />,
    path: '/employees',
    badge: 'ALL'
  },
  {
    id: 'finance',
    label: 'Chart of Accounts',
    icon: <FinanceIcon />,
    path: '/finance',
    badge: 'NEW'
  },
  {
    id: 'transactions',
    label: 'Transaction Entry',
    icon: <TransactionIcon />,
    path: '/transactions',
    badge: 'NEW'
  },
  {
    id: 'reports',
    label: 'Financial Reports',
    icon: <ReportsIcon />,
    path: '/reports',
    badge: 'NEW'
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: <CustomersIcon />,
    path: '/customers'
  },
  {
    id: 'invoices',
    label: 'Invoices',
    icon: <InvoicesIcon />,
    path: '/invoices'
  },
  {
    id: 'expenses',
    label: 'Expenses',
    icon: <ExpensesIcon />,
    path: '/expenses'
  },
  {
    id: 'vat',
    label: 'VAT Returns',
    icon: <VATIcon />,
    path: '/vat',
    badge: 'MTD'
  },
  {
    id: 'hmrc',
    label: 'HMRC Integration',
    icon: <UKFlagIcon />,
    path: '/hmrc',
    badge: 'MTD'
  },
  {
    id: 'hmrc-pay',
    label: 'Pay HMRC',
    icon: <BankingIcon />,
    path: '/hmrc/pay',
    badge: 'NEW'
  },
  {
    id: 'cis',
    label: 'CIS (Construction)',
    icon: <IntegrationIcon />,
    path: '/cis',
    badge: 'CIS'
  },
  {
    id: 'banking',
    label: 'Banking',
    icon: <BankingIcon />,
    path: '/banking',
    badge: 'Open Banking'
  },
  {
    id: 'advanced-reports',
    label: 'Advanced Reports',
    icon: <ReportsIcon />,
    path: '/advanced-reports',
    badge: 'NEW'
  },
  {
    id: 'companies-house',
    label: 'Companies House',
    icon: <CompaniesHouseIcon />,
    path: '/companies-house',
    badge: 'NEW'
  },
  {
    id: 'integration',
    label: 'Integration',
    icon: <IntegrationIcon />,
    path: '/integration',
    badge: 'Cross-Domain'
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: <MonitoringIcon />,
    path: '/monitoring',
    badge: 'NEW'
  },
  // Security Dashboard hidden from end users - access via direct URL /security for admins
  // {
  //   id: 'security',
  //   label: 'Security',
  //   icon: <SecurityIcon />,
  //   path: '/security',
  //   badge: 'Enterprise'
  // },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: <ComplianceIcon />,
    path: '/compliance',
    badge: 'UK'
  },
  {
    id: 'audit',
    label: 'Audit Log',
    icon: <AuditIcon />,
    path: '/audit',
    badge: 'Enterprise'
  },
  {
    id: 'alerts',
    label: 'Alerts',
    icon: <AlertsIcon />,
    path: '/alerts',
    badge: 'BI'
  },
  {
    id: 'backup',
    label: 'Backup & Recovery',
    icon: <BackupIcon />,
    path: '/backup',
    badge: 'Enterprise'
  },
  {
    id: 'data-retention',
    label: 'Data Retention',
    icon: <DataRetentionIcon />,
    path: '/data-retention',
    badge: 'GDPR'
  },
  {
    id: 'users',
    label: 'User Management',
    icon: <UsersIcon />,
    path: '/users',
    badge: 'RBAC'
  }
];

// Executive Suite Apps - External Links
const executiveSuiteApps: NavigationItem[] = [
  {
    id: 'executive-ai',
    label: 'Executive Force AI',
    icon: <ExecutiveAIIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3003',
    badge: 'AI Suite'
  },
  {
    id: 'crm',
    label: 'EF-CRM',
    icon: <CRMIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3001',
    badge: 'CRM'
  },
  {
    id: 'ascin',
    label: 'ASCIN Supply Chain',
    icon: <SupplyChainIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3004',
    badge: 'Supply'
  },
  {
    id: 'project-mgmt',
    label: 'Project Management',
    icon: <ProjectManagementIcon />,
    path: '#',
    external: true,
    externalUrl: 'http://localhost:3005',
    badge: 'PM'
  }
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, firebaseUser } = useAuthContext();

  const displayName = user?.name || firebaseUser?.displayName || firebaseUser?.email || 'User';
  const displayEmail = user?.email || firebaseUser?.email || '';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigation = (path: string, external?: boolean, externalUrl?: string) => {
    if (external && externalUrl) {
      window.open(externalUrl, '_blank');
    } else {
      navigate(path);
    }
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <UKFlagIcon sx={{ color: '#dc004e' }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            EF-CapFlow
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Modern UK Financial Management for SMEs
        </Typography>
        <Chip
          label="UK Optimized"
          size="small"
          color="secondary"
          sx={{ mt: 1 }}
        />
      </Box>

      <Divider />

      {/* Navigation */}
      <List sx={{ px: 2, py: 1 }}>
        {navigationItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path, item.external, item.externalUrl)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: location.pathname === item.path ? 'white' : 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                    color: location.pathname === item.path ? 'white' : 'primary.main',
                    borderColor: location.pathname === item.path ? 'white' : 'primary.main',
                  }}
                />
              )}
              {item.comingSoon && (
                <Chip
                  label="Soon"
                  size="small"
                  color="warning"
                  sx={{
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ mt: 2 }} />

      {/* Executive Suite Apps - Now handled by BusinessSuiteNavigation in header */}
      {/* Keeping this section commented for reference
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ px: 1 }}>
          Executive Suite
        </Typography>
        <List sx={{ py: 0 }}>
          {executiveSuiteApps.map((app) => (
            <ListItem key={app.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(app.path, app.external, app.externalUrl)}
                sx={{
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: 'text.secondary',
                  }}
                >
                  {app.icon}
                </ListItemIcon>
                <ListItemText
                  primary={app.label}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 400,
                  }}
                />
                {app.badge && (
                  <Chip
                    label={app.badge}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.65rem',
                      height: 18,
                      color: 'secondary.main',
                      borderColor: 'secondary.main',
                    }}
                  />
                )}
                {app.external && (
                  <ExternalLinkIcon sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary' }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      */}

      {/* Divider removed since Executive Suite section is commented out */}

      {/* Help & Support Section */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Help & Support
        </Typography>
        <List sx={{ py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/user-guide')}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary="User Guide"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/faq')}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemText
                primary="FAQs"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => navigate('/privacy-policy')}
              sx={{ borderRadius: 1 }}
            >
              <ListItemText
                primary="Privacy Policy"
                primaryTypographyProps={{ fontSize: '0.85rem' }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Divider />

      {/* UK Features Highlight */}
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          UK Features
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Chip label="HMRC MTD Ready" size="small" color="success" variant="outlined" />
          <Chip label="Companies House" size="small" color="info" variant="outlined" />
          <Chip label="UK VAT Compliant" size="small" color="primary" variant="outlined" />
          <Chip label="Open Banking" size="small" color="secondary" variant="outlined" />
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Designed specifically for UK SMEs with full compliance and modern features.
        </Typography>
      </Box>

      {/* Competitive Advantage */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', mt: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          🚀 Competitive Advantage
        </Typography>
        <Typography variant="caption">
          Cross-domain integration with CRM and Supply Chain for unified business intelligence.
        </Typography>
      </Box>

      {/* User Profile + Sign Out — pinned to bottom */}
      <Box sx={{ mt: 'auto' }}>
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.85rem' }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {displayEmail}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <IconButton size="small" onClick={handleLogout} color="inherit">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
